import React, { useState, useEffect } from 'react';
import { 
  CloseIcon, SparkleIcon, FeelIcon, ObserveIcon, NeedIcon, 
  StarIcon, RefreshIcon, InsightIcon, QuoteIcon 
} from './BespokeIcons';

/**
 * NVC Wisdom Popups - Periodic reminders of key principles
 * 
 * These appear as gentle, non-intrusive notifications that reinforce
 * the core philosophy without being preachy.
 */

// Collection of wisdom nuggets organized by category
const wisdomNuggets = {
  core: [
    {
      text: "It's not about being right or nice—it's about everyone walking away with a win.",
      icon: StarIcon,
      color: 'amber'
    },
    {
      text: "Don't get bogged down in the dogma. The goal is genuine shared consciousness.",
      icon: SparkleIcon,
      color: 'purple'
    },
    {
      text: "You don't need to become an expert. Just be present with empathy.",
      icon: FeelIcon,
      color: 'rose'
    },
    {
      text: "The craziest thing is that love actually alchemizes conflict into gold.",
      icon: StarIcon,
      color: 'amber'
    },
    {
      text: "Reducing suffering and achieving solutions—that's the only goal here.",
      icon: InsightIcon,
      color: 'emerald'
    },
    {
      text: "When we choose to do things, we are more likely to proceed. When we feel we must, we resist.",
      icon: InsightIcon,
      color: 'emerald'
    },
    {
      text: "It always comes back to the golden rule: do unto others as you would want done to you.",
      icon: StarIcon,
      color: 'amber'
    },
    {
      text: "Before Building, Build Belonging... that's ElchmE!",
      icon: FeelIcon,
      color: 'rose'
    },
    {
      text: "Create unmistakable, authentic concern—the other person can't help but become empathetic to you.",
      icon: FeelIcon,
      color: 'rose'
    },
    {
      text: "Have these conversations with mutual tenderness regularly. Regularly clear out conflict.",
      icon: SparkleIcon,
      color: 'purple'
    },
    {
      text: "Our needs are related to the divine energy flowing through us.",
      icon: NeedIcon,
      color: 'emerald'
    }
  ],
  observe: [
    {
      text: "What would a camera record? Just the facts, no interpretations.",
      icon: ObserveIcon,
      color: 'blue'
    },
    {
      text: "Separate what you saw from the story you told yourself about it.",
      icon: ObserveIcon,
      color: 'blue'
    },
    {
      text: "The highest form of Human Intelligence is to observe yourself without judgement. —Krishnamurti",
      icon: ObserveIcon,
      color: 'blue'
    }
  ],
  feel: [
    {
      text: "Feelings are messengers. What is this emotion trying to tell you?",
      icon: FeelIcon,
      color: 'red'
    },
    {
      text: "'I feel that you...' is not a feeling—it's a thought in disguise.",
      icon: FeelIcon,
      color: 'red'
    },
    {
      text: "Vulnerability is the birthplace of connection.",
      icon: FeelIcon,
      color: 'red'
    }
  ],
  need: [
    {
      text: "Behind every feeling is a universal human need.",
      icon: NeedIcon,
      color: 'emerald'
    },
    {
      text: "Needs are never in conflict—only strategies are.",
      icon: NeedIcon,
      color: 'emerald'
    },
    {
      text: "When we hear the need behind the words, everything changes.",
      icon: NeedIcon,
      color: 'emerald'
    }
  ],
  dance: [
    {
      text: "The NVC dance: Honesty → Empathy → Honesty → Empathy... keep flowing.",
      icon: RefreshIcon,
      color: 'purple'
    },
    {
      text: "Sometimes you need to give empathy before you can receive it.",
      icon: RefreshIcon,
      color: 'purple'
    },
    {
      text: "Emergency empathy first: when someone's upset, listen before explaining.",
      icon: RefreshIcon,
      color: 'purple'
    }
  ],
  agency: [
    {
      text: "Before you feel compelled, be curious. What do you really want? What do you choose?",
      icon: StarIcon,
      color: 'amber'
    },
    {
      text: "Our need for agency—the ability to choose our own path—is fundamental to being human.",
      icon: StarIcon,
      color: 'purple'
    },
    {
      text: "Even compliments can feel controlling when they come from external judgment rather than inner connection.",
      icon: InsightIcon,
      color: 'emerald'
    },
    {
      text: "Every action is a choice. 'I have to go to work' becomes 'I choose to go because I value...'",
      icon: InsightIcon,
      color: 'amber'
    },
    {
      text: "Notice words like 'should', 'must', 'have to'—they often create resistance, not motivation.",
      icon: ObserveIcon,
      color: 'blue'
    },
    {
      text: "What do you choose? Not what you should do, but what you genuinely want to contribute.",
      icon: StarIcon,
      color: 'purple'
    }
  ],
  power: [
    {
      text: "Power imbalance will almost always lead to failure to connect. Seek equality first.",
      icon: InsightIcon,
      color: 'amber'
    },
    {
      text: "Whether you hold power or lack it—bring yourself to equal ground for this to work.",
      icon: StarIcon,
      color: 'purple'
    },
    {
      text: "Connection requires balance. If you're in a power state, consciously level the field.",
      icon: InsightIcon,
      color: 'emerald'
    },
    {
      text: "True empathy can't flow uphill or downhill—only between equals.",
      icon: FeelIcon,
      color: 'rose'
    },
    {
      text: "Before seeking to be understood, ensure they feel safe to speak freely.",
      icon: ObserveIcon,
      color: 'blue'
    },
    {
      text: "The person with more power must create safety. The person with less must claim their voice.",
      icon: StarIcon,
      color: 'amber'
    }
  ],
  vulnerability: [
    {
      text: "Start with empathy before interpreting or diagnosing.",
      icon: FeelIcon,
      color: 'purple'
    },
    {
      text: "Show some of yourself. Vulnerability opens doors that logic can't.",
      icon: InsightIcon,
      color: 'rose'
    },
    {
      text: "Before advising, just listen. Before fixing, just feel.",
      icon: FeelIcon,
      color: 'blue'
    },
    {
      text: "The most powerful thing you can say: 'Help me understand how you feel.'",
      icon: QuoteIcon,
      color: 'amber'
    },
    {
      text: "Diagnosis creates distance. Empathy creates connection.",
      icon: InsightIcon,
      color: 'emerald'
    },
    {
      text: "Your vulnerability gives them permission to be vulnerable too.",
      icon: FeelIcon,
      color: 'rose'
    },
    {
      text: "Lead with 'I wonder if you're feeling...' not 'You should...'",
      icon: ObserveIcon,
      color: 'purple'
    }
  ],
  universal: [
    {
      text: "You will ALWAYS achieve some success when you achieve empathy for self, other, and both.",
      icon: StarIcon,
      color: 'amber'
    },
    {
      text: "Even in the hardest situations, mutual empathy creates miracles.",
      icon: SparkleIcon,
      color: 'purple'
    },
    {
      text: "You don't need them to change. You need mutual understanding.",
      icon: InsightIcon,
      color: 'emerald'
    },
    {
      text: "The goal isn't winning. It's both of you feeling truly heard.",
      icon: FeelIcon,
      color: 'rose'
    },
    {
      text: "When empathy is universal, gold naturally emerges.",
      icon: StarIcon,
      color: 'amber'
    },
    {
      text: "Hold both realities simultaneously—yours and theirs. This is where connection lives.",
      icon: InsightIcon,
      color: 'purple'
    }
  ]
};

// Get a random wisdom based on current step
export const getWisdomForStep = (step) => {
  let category = 'core';
  if (step === 'blue' || step === 1) category = 'observe';
  else if (step === 'red' || step === 2) category = 'feel';
  else if (step === 'green' || step === 3) category = 'need';
  else if (step === 'understand' || step === 4) category = 'dance';
  
  // Include power, vulnerability, and universal wisdom in all rotations for profound guidance
  const nuggets = [
    ...wisdomNuggets[category], 
    ...wisdomNuggets.core, 
    ...wisdomNuggets.agency,
    ...wisdomNuggets.power,
    ...wisdomNuggets.vulnerability,
    ...wisdomNuggets.universal
  ];
  return nuggets[Math.floor(Math.random() * nuggets.length)];
};

/**
 * WisdomPopup - A gentle, floating reminder
 */
export const WisdomPopup = ({ wisdom, onDismiss, autoHide = true }) => {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    // Fade in
    setTimeout(() => setVisible(true), 100);
    
    // Auto-hide after 8 seconds
    if (autoHide) {
      const timer = setTimeout(() => {
        setExiting(true);
        setTimeout(() => onDismiss?.(), 500);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [autoHide, onDismiss]);

  if (!wisdom) return null;

  const IconComponent = wisdom.icon || SparkleIcon;
  const colorMap = {
    amber: 'from-amber-500/20 to-amber-600/10 border-amber-500/30 text-amber-300',
    purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30 text-purple-300',
    rose: 'from-rose-500/20 to-rose-600/10 border-rose-500/30 text-rose-300',
    blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-300',
    red: 'from-red-500/20 to-red-600/10 border-red-500/30 text-red-300',
    emerald: 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/30 text-emerald-300',
  };

  const colors = colorMap[wisdom.color] || colorMap.amber;

  return (
    <div 
      className={`fixed bottom-24 right-4 max-w-xs sm:max-w-sm z-40 transition-all duration-500 ${
        visible && !exiting ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
      data-testid="wisdom-popup"
    >
      <div className={`bg-gradient-to-br ${colors} backdrop-blur-md rounded-xl p-3 sm:p-4 border shadow-lg`}>
        <button 
          onClick={() => {
            setExiting(true);
            setTimeout(() => onDismiss?.(), 300);
          }}
          className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/10 transition-colors"
        >
          <CloseIcon size={12} color="rgba(255,255,255,0.4)" />
        </button>
        
        <div className="flex items-start gap-2 sm:gap-3">
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
            <IconComponent size={14} color={
              wisdom.color === 'amber' ? '#FBBF24' :
              wisdom.color === 'purple' ? '#A855F7' :
              wisdom.color === 'rose' ? '#F43F5E' :
              wisdom.color === 'red' ? '#EF4444' :
              wisdom.color === 'emerald' ? '#10B981' :
              wisdom.color === 'blue' ? '#3B82F6' : '#FBBF24'
            } />
          </div>
          <p className="text-xs sm:text-sm leading-relaxed pr-3 sm:pr-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            "{wisdom.text}"
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * WisdomManager - Controls when and how wisdom popups appear
 */
export const useWisdomManager = (currentStep, isActive = true) => {
  const [currentWisdom, setCurrentWisdom] = useState(null);
  const [lastShown, setLastShown] = useState(0);

  // Show wisdom periodically (every 90 seconds) when active - 50% less frequent
  useEffect(() => {
    if (!isActive) return;

    const showWisdom = () => {
      const now = Date.now();
      if (now - lastShown > 90000) { // At least 90 seconds between popups (50% less frequent)
        setCurrentWisdom(getWisdomForStep(currentStep));
        setLastShown(now);
      }
    };

    // Initial delay of 30 seconds before first wisdom
    const initialTimer = setTimeout(showWisdom, 30000);
    
    // Then show periodically
    const interval = setInterval(showWisdom, 60000 + Math.random() * 30000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [currentStep, isActive, lastShown]);

  const dismissWisdom = () => setCurrentWisdom(null);
  const showRandomWisdom = () => setCurrentWisdom(getWisdomForStep(currentStep));

  return { currentWisdom, dismissWisdom, showRandomWisdom };
};

export default WisdomPopup;
